import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath, soccerUrl } from '../utils/paths';
import { canFormOrEditTeams } from '../utils/practiceSchedule';
import SoccerFieldVisualization from './SoccerFieldVisualization';
import {
  groupPlayersByPosition,
  getTeamDisplayName,
  getTeamAccent,
  getPositionAbbreviation,
} from '../utils/lineupLayout';
import { mediaUrl } from '../utils/blogUtils';
import './PracticeLineupView.css';

const PracticeLineupView = () => {
  const { id: practiceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lineup, setLineup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const loadLineup = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError('');
      const data = await api.getPracticeLineup(practiceId, user.id);
      setLineup(data);
    } catch (err) {
      setError(err.message || 'Failed to load team lineup');
    } finally {
      setLoading(false);
    }
  }, [practiceId, user?.id]);

  useEffect(() => {
    loadLineup();
  }, [loadLineup]);

  const handleEnsureShare = async () => {
    if (!user?.id) return null;
    try {
      const share = await api.ensurePracticeFormationShare(practiceId, user.id);
      if (share?.formationShareToken) {
        setLineup((prev) =>
          prev
            ? {
                ...prev,
                formationShareToken: share.formationShareToken,
                sharePath: share.sharePath,
              }
            : prev
        );
        return share.formationShareToken;
      }
    } catch (err) {
      setError(err.message || 'Failed to create share link');
    }
    return lineup?.formationShareToken || null;
  };

  const handleCopyLink = async () => {
    const token = lineup?.formationShareToken || (await handleEnsureShare());
    if (!token) return;
    const link = soccerUrl(`/public/practice-formation/${token}`);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.prompt('Copy this link:', link);
    }
  };

  const handleShareWhatsApp = async () => {
    const token = lineup?.formationShareToken || (await handleEnsureShare());
    if (!token || !lineup?.practice) return;
    const url = api.sharePracticeFormationViaWhatsApp({
      title: lineup.practice.title,
      practiceDate: lineup.practice.practiceDate,
      location: lineup.practice.location,
      shareToken: token,
      clubName: lineup.clubName,
      teamsFormed: !!lineup?.practice?.teamsPublishedToPublic,
    });
    window.open(url, '_blank');
  };

  const handlePublishTeams = async (published) => {
    if (!user?.id) return;
    try {
      setPublishing(true);
      setError('');
      const updated = await api.setTeamsPublishedToPublic(practiceId, user.id, published);
      setLineup((prev) => (prev ? { ...prev, practice: updated } : prev));
    } catch (err) {
      setError(err.message || 'Failed to update public team visibility');
    } finally {
      setPublishing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="loading">Loading team lineup…</div>;
  }

  if (error) {
    return (
      <div className="practice-lineup">
        <div className="alert alert-error">{error}</div>
        <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>
    );
  }

  if (!lineup?.team) {
    const sharePublicFromLineup = async () => {
      const token = await handleEnsureShare();
      if (!token || !lineup?.practice) return;
      const url = api.sharePracticeFormationViaWhatsApp({
        title: lineup.practice.title,
        practiceDate: lineup.practice.practiceDate,
        location: lineup.practice.location,
        shareToken: token,
        clubName: lineup.clubName,
        teamsFormed: false,
      });
      window.open(url, '_blank');
    };

    return (
      <div className="practice-lineup">
        <div className="practice-lineup__empty card">
          <h2>Lineup not ready yet</h2>
          <p>Teams have not been formed for this practice. You can still share the public page so members see voting status.</p>
          {lineup?.canManage && (
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '1rem' }}>
              <Link to={soccerPath(`/practices/${practiceId}/teams`)} className="btn btn-primary">
                Form teams
              </Link>
              <button type="button" className="btn btn-whatsapp" onClick={sharePublicFromLineup}>
                Share public page
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCopyLink}>
                {copied ? 'Link copied!' : 'Copy public link'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const { practice, team, clubName, canManage } = lineup;
  const divisions = team.divisions || [];
  const canEditTeams = canManage && canFormOrEditTeams(practice);

  return (
    <div className="practice-lineup">
      <div className="practice-lineup__toolbar">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className="practice-lineup__toolbar-actions">
          {canManage && (
            <>
              <Link to={soccerPath(`/practices/${practiceId}/teams`)} className="btn btn-primary">
                {canEditTeams ? 'Edit lineup & positions' : 'View lineup & positions'}
              </Link>
              {team && canEditTeams && (
                <button
                  type="button"
                  className={`btn ${practice.teamsPublishedToPublic ? 'btn-secondary' : 'btn-success'}`}
                  onClick={() => handlePublishTeams(!practice.teamsPublishedToPublic)}
                  disabled={publishing}
                >
                  {practice.teamsPublishedToPublic ? 'Unpublish public teams' : 'Publish teams publicly'}
                </button>
              )}
              <button type="button" className="btn btn-whatsapp" onClick={handleShareWhatsApp}>
                Share via WhatsApp
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCopyLink}>
                {copied ? 'Link copied!' : 'Copy share link'}
              </button>
            </>
          )}
          {lineup.formationShareToken && (
            <Link
              to={soccerPath(`/public/practice-formation/${lineup.formationShareToken}`)}
              target="_blank"
              className="btn btn-secondary"
            >
              Public view
            </Link>
          )}
        </div>
      </div>

      <header className="practice-lineup__header">
        <p className="practice-lineup__club">{clubName}</p>
        <h1>{practice.title}</h1>
        <div className="practice-lineup__meta">
          {practice.practiceDate && <span>📅 {formatDate(practice.practiceDate)}</span>}
          {practice.location && <span>📍 {practice.location}</span>}
        </div>
      </header>

      {canManage && team && (
        <div
          className={`practice-lineup__publish-banner ${practice.teamsPublishedToPublic ? 'practice-lineup__publish-banner--live' : ''}`}
        >
          {practice.teamsPublishedToPublic
            ? 'Teams are published on the public practice page.'
            : 'Teams are draft only — publish when the lineup and formations are final.'}
        </div>
      )}

      {divisions.length >= 2 && (
        <div className="practice-lineup__matchup-banner" aria-hidden="true">
          <span className="practice-lineup__matchup-team practice-lineup__matchup-team--red">
            {getTeamDisplayName(divisions[0])}
          </span>
          <span className="practice-lineup__matchup-vs">vs</span>
          <span className="practice-lineup__matchup-team practice-lineup__matchup-team--blue">
            {getTeamDisplayName(divisions[1])}
          </span>
        </div>
      )}

      <section className="practice-lineup__pitch card">
        <h2>Formation</h2>
        <SoccerFieldVisualization
          teams={team}
          practice={practice}
          readOnly={!canManage}
          layout="vertical"
          showPlayerPhotos
          alwaysVisible
          initialShowField
        />
      </section>

      <section className="practice-lineup__squads">
        <h2>Squad lineups</h2>
        <div className="practice-lineup__squad-grid">
          {divisions.map((division, index) => {
            const accent = getTeamAccent(division);
            const groups = groupPlayersByPosition(division.players || []);
            return (
              <article
                key={`${division.teamName}-${index}`}
                className="practice-lineup__squad-card"
                style={{
                  borderColor: accent.border,
                  backgroundColor: accent.bg,
                }}
              >
                <header className="practice-lineup__squad-header">
                  <h3 style={{ color: accent.color }}>{getTeamDisplayName(division)}</h3>
                  {division.formation && (
                    <span className="practice-lineup__formation-pill">{division.formation}</span>
                  )}
                  <span className="practice-lineup__player-count">
                    {(division.players || []).length} players
                  </span>
                </header>

                {groups.map((group) => (
                  <div key={group.position} className="practice-lineup__position-group">
                    <h4>{group.label}</h4>
                    <ul>
                      {group.players.map((player) => {
                        const photoUrl = player.profilePictureUrl
                          ? mediaUrl(player.profilePictureUrl)
                          : null;
                        return (
                        <li key={player.id}>
                          {photoUrl ? (
                            <img
                              src={photoUrl}
                              alt=""
                              className="practice-lineup__player-avatar"
                            />
                          ) : (
                            <span className="practice-lineup__player-avatar practice-lineup__player-avatar--placeholder">
                              {(player.name || '?').charAt(0).toUpperCase()}
                            </span>
                          )}
                          <span className="practice-lineup__player-name">{player.name}</span>
                          <span className="practice-lineup__player-pos">
                            {getPositionAbbreviation(player.position)}
                          </span>
                        </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </article>
            );
          })}
        </div>

        {team.reservePlayers && team.reservePlayers.length > 0 && (
          <section className="practice-lineup__reserve card">
            <h2>Reserve players</h2>
            <ul className="practice-lineup__reserve-list">
              {team.reservePlayers.map((player) => (
                <li key={player.id}>
                  <span className="practice-lineup__player-name">{player.name}</span>
                  <span className="practice-lineup__reserve-badge">Reserve</span>
                  <span className="practice-lineup__player-pos">
                    {getPositionAbbreviation(player.position)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </section>
    </div>
  );
};

export default PracticeLineupView;
